'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const PRO_IMAGES = [
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_13d754aa4-1763296558802.png", imgAlt: 'Marcus Thompson, professional male plumber, smiling photo', name: 'Marcus Thompson', rating: 4.9, jobs: 312 },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1a62c4bce-1763301484887.png", imgAlt: 'Destiny Williams, professional female electrician, confident photo', name: 'Destiny Williams', rating: 4.8, jobs: 198 },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_137ad5a3e-1765083592272.png", imgAlt: 'Rafael Moreno, professional HVAC technician, friendly photo', name: 'Rafael Moreno', rating: 5.0, jobs: 87 },
];

export default function RateSection() {
  const { language } = useLanguage();
  const t = getTranslations(language)?.rate;

  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [selectedRating, setSelectedRating] = useState(5);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref?.current) obs?.observe(ref?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section id="rate" ref={ref} className="section-glow py-32 px-6 bg-dispatch-surface/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: Pro network cards */}
          <div className={`${visible ? 'reveal-blur is-visible' : 'reveal-blur'}`}>
            <div className="space-y-4">
              {PRO_IMAGES?.map((pro, i) => {
                const proT = t?.pros?.[i];
                return (
                  <div
                    key={pro?.name}
                    className={`shine-card glass-panel rounded-2xl p-5 border border-dispatch-surface hover:border-dispatch-cyan/30 transition-all duration-500 stagger-grid ${visible ? 'is-visible' : ''}`}
                    style={{ transitionDelay: `${i * 0.1}s` }}>
                    <div className="flex items-start gap-4">
                      <AppImage
                        src={pro?.img}
                        alt={pro?.imgAlt}
                        width={48}
                        height={48}
                        className="rounded-full object-cover ring-2 ring-dispatch-surface" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-manrope font-semibold text-dispatch-text">{pro?.name}</p>
                          <span className="chip shrink-0 text-[9px]">{proT?.badge}</span>
                        </div>
                        <p className="text-dispatch-muted font-mono text-xs mt-0.5">{proT?.role}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5]?.map((s) => (
                              <svg key={s} width="10" height="10" viewBox="0 0 24 24"
                                fill={s <= Math.round(pro?.rating) ? '#FFB800' : 'rgba(255,184,0,0.2)'}>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                            ))}
                            <span className="text-dispatch-text font-manrope font-bold text-xs ml-1">{pro?.rating}</span>
                          </div>
                          <span className="text-dispatch-faint font-mono text-[10px]">{pro?.jobs} {language === 'fr' ? 'missions' : language === 'ar' ? 'مهمة' : 'jobs'}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {proT?.tags?.map((tag) => (
                            <span key={tag} className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-dispatch-bg border border-dispatch-surface text-dispatch-muted">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: copy + rating UI */}
          <div className={`flex flex-col gap-8 ${visible ? 'reveal-up is-visible' : 'reveal-up'}`}>
            <div>
              <span className="chip mb-4 inline-flex">{t?.chip}</span>
              <h2 className="font-manrope font-extrabold text-dispatch-text leading-tight tracking-tight mt-4"
                style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
                {t?.headline1}<br />
                <span className="text-dispatch-cyan">{t?.headline2}</span><br />
                {t?.headline3}
              </h2>
              <p className="text-dispatch-muted text-lg font-dm-sans leading-relaxed mt-4 max-w-md">
                {t?.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {t?.chips?.map((c) => <span key={c} className="chip">{c}</span>)}
            </div>

            <div className="glass-panel rounded-2xl p-6 border border-dispatch-cyan/20">
              {!submitted ? (
                <>
                  <p className="text-dispatch-text font-manrope font-semibold mb-1">{t?.rateTitle}</p>
                  <p className="text-dispatch-muted font-mono text-xs mb-4">{t?.rateSubtitle}</p>
                  <div className="flex items-center gap-3 mb-5">
                    {[1, 2, 3, 4, 5]?.map((s) => (
                      <button key={s} onClick={() => setSelectedRating(s)} className="transition-transform hover:scale-125 duration-200">
                        <svg width="28" height="28" viewBox="0 0 24 24"
                          fill={s <= selectedRating ? '#FFB800' : 'rgba(255,184,0,0.15)'}>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      </button>
                    ))}
                    <span className="text-dispatch-cyan font-manrope font-bold text-lg ml-2">{selectedRating}.0</span>
                  </div>
                  <button
                    onClick={() => setSubmitted(true)}
                    className="w-full py-3 bg-dispatch-cyan text-dispatch-bg rounded-xl font-bold font-mono text-sm hover:bg-white transition-colors shadow-cyan-sm">
                    {t?.submitBtn}
                  </button>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full bg-dispatch-cyan/20 border border-dispatch-cyan/40 flex items-center justify-center mx-auto mb-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00D4FF" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="text-dispatch-cyan font-manrope font-bold">{t?.submitted}</p>
                  <p className="text-dispatch-muted font-mono text-xs mt-1">{t?.submittedSub}</p>
                </div>
              )}
            </div>

            <a href="#download" className="self-start flex items-center gap-3 px-6 py-3.5 bg-dispatch-cyan text-dispatch-bg rounded-xl font-bold font-mono text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-cyan-sm">
              {t?.cta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
